import {
  publicationsByNetwork,
  SOCIAL_NETWORK_LABELS,
  SOCIAL_PIECE_STATUS_LABELS,
  SOCIAL_PRIORITY_LABELS,
  SOCIAL_STEP_LABELS,
  SOCIAL_STEPS,
  workByPerson,
  type SocialPiece,
  type SocialPieceStatus,
  type SocialPlan,
} from '@growup/site-shared';
import { Button, cx, StatusBadge } from '@growup/ui';
import { useState, type DragEvent } from 'react';

import { PIECE_TONE } from './PlansView';

const dmy = (day: string) => day.split('-').reverse().join('/');

/** As colunas do Quadro (RF-RS10), sem as abandonadas, que ficam em baixo. */
const COLUMNS: SocialPieceStatus[] = [
  'to_film',
  'to_edit',
  'to_approve',
  'to_publish',
  'published',
];

/** O que "passar a coluna seguinte" faz, para quem pede. `null`: abre a peca. */
export interface Advance {
  label: string;
  method: string;
  path: string;
  body: unknown;
}

export function advanceOf(piece: SocialPiece, canManage: boolean): Advance | null {
  const base = `/pieces/${piece.id}`;
  const mark = (step: string): Advance => ({
    label: `${SOCIAL_STEP_LABELS[step as keyof typeof SOCIAL_STEP_LABELS]} feita`,
    method: 'POST',
    path: `${base}/steps/${step}/done`,
    body: { done: true },
  });
  switch (piece.status) {
    case 'to_film':
      return mark('filming');
    case 'to_edit':
      return mark('editing');
    case 'to_approve':
      return canManage
        ? { label: 'Aprovar', method: 'POST', path: `${base}/review`, body: { approved: true } }
        : null;
    case 'to_publish':
      // Com redes alvo, a publicacao pede os links: abre-se a peca.
      return piece.networks.length === 0 ? mark('publishing') : null;
    default:
      return null;
  }
}

/** Lista: uma linha por peca, com quem fez cada passo. */
export function PiecesTable({
  pieces,
  onOpen,
}: {
  pieces: SocialPiece[];
  onOpen: (id: string) => void;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[40rem] text-left text-caption">
        <thead>
          <tr className="border-b-2 border-gu-line">
            <th className="p-2">Peça</th>
            <th className="p-2">Estado</th>
            {SOCIAL_STEPS.map((s) => (
              <th key={s} className="p-2">
                {SOCIAL_STEP_LABELS[s]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {pieces.map((p) => (
            <tr key={p.id} className="border-b border-gu-line align-top">
              <td className="p-2">
                <button
                  type="button"
                  className="text-left font-semibold underline"
                  onClick={() => onOpen(p.id)}
                >
                  {p.title}
                </button>
                <div className="text-gu-ink-muted">
                  {SOCIAL_PRIORITY_LABELS[p.priority]}
                  {p.publishOn ? ` · ${dmy(p.publishOn)}` : ''}
                  {p.eventDay ? ` · evento ${dmy(p.eventDay)}` : ''}
                  {p.zone ? ` · ${p.zone.name}` : ''}
                </div>
              </td>
              <td className="p-2">
                <StatusBadge tone={PIECE_TONE[p.status]}>
                  {SOCIAL_PIECE_STATUS_LABELS[p.status]}
                </StatusBadge>
              </td>
              {p.steps.map((s) => (
                <td key={s.step} className={cx('p-2', s.overdue && 'text-gu-danger')}>
                  {s.notApplicable
                    ? '– não se aplica'
                    : s.done
                      ? `✓ ${s.doneBy ?? '—'}`
                      : s.assignee
                        ? `○ ${s.assignee.login}`
                        : '○'}
                  {s.step === 'publishing' && p.networks.length > 0 && !s.done
                    ? ` (${String(p.publications.length)}/${String(p.networks.length)})`
                    : ''}
                  {s.overdue && s.dueOn ? ` · prazo ${dmy(s.dueOn)}` : ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Quadro: uma coluna por estado. Arrastar um cartao para a coluna seguinte (ou
 * o botao no cartao, para quem nao usa rato) marca o passo como feito por mim.
 */
export function PiecesBoard({
  plan,
  busy,
  onOpen,
  onAdvance,
}: {
  plan: SocialPlan;
  busy: boolean;
  onOpen: (id: string) => void;
  onAdvance: (a: Advance) => void;
}) {
  const [dragging, setDragging] = useState<string | null>(null);
  const abandoned = plan.pieces.filter((p) => p.status === 'abandoned');

  const drop = (column: SocialPieceStatus) => (e: DragEvent) => {
    e.preventDefault();
    const piece = plan.pieces.find((p) => p.id === dragging);
    setDragging(null);
    if (!piece) return;
    const next = COLUMNS[COLUMNS.indexOf(piece.status) + 1];
    const advance = advanceOf(piece, plan.canManage);
    if (column === next && advance) onAdvance(advance);
    else if (column !== piece.status) onOpen(piece.id);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="grid gap-3 md:grid-cols-5">
        {COLUMNS.map((column) => {
          const cards = plan.pieces.filter((p) => p.status === column);
          return (
            <section
              key={column}
              aria-label={SOCIAL_PIECE_STATUS_LABELS[column]}
              onDragOver={(e) => e.preventDefault()}
              onDrop={drop(column)}
              className="flex min-h-24 flex-col gap-2 bg-gu-sunken p-2"
            >
              <h3 className="font-sub text-label font-bold uppercase">
                {SOCIAL_PIECE_STATUS_LABELS[column]} ({cards.length})
              </h3>
              {cards.map((p) => {
                const advance = advanceOf(p, plan.canManage);
                return (
                  <article
                    key={p.id}
                    draggable={!busy}
                    onDragStart={() => setDragging(p.id)}
                    onDragEnd={() => setDragging(null)}
                    className="flex cursor-grab flex-col gap-1 border-2 border-gu-line bg-gu-raised p-2"
                  >
                    <button
                      type="button"
                      className="text-left font-semibold"
                      onClick={() => onOpen(p.id)}
                    >
                      {p.title}
                    </button>
                    <span className="text-caption text-gu-ink-muted">
                      {p.priority === 'must' ? 'Obrigatória' : 'Se houver tempo'}
                      {p.publishOn ? ` · ${dmy(p.publishOn)}` : ''}
                      {p.steps.some((s) => s.overdue) ? ' · prazo passado' : ''}
                    </span>
                    {advance ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={busy}
                        onClick={() => onAdvance(advance)}
                      >
                        {advance.label} →
                      </Button>
                    ) : null}
                  </article>
                );
              })}
            </section>
          );
        })}
      </div>
      {abandoned.length > 0 ? (
        <p className="text-caption text-gu-ink-muted">
          Abandonadas:{' '}
          {abandoned.map((p, i) => (
            <span key={p.id}>
              {i > 0 ? ', ' : ''}
              <button type="button" className="underline" onClick={() => onOpen(p.id)}>
                {p.title}
              </button>
            </span>
          ))}
        </p>
      ) : null}
    </div>
  );
}

/** Por pessoa e publicacoes por rede, neste plano (RF-RS10, RF-RS11). */
export function PlanWork({ plan }: { plan: SocialPlan }) {
  const people = workByPerson(plan.pieces);
  const networks = publicationsByNetwork(plan.pieces);
  if (people.length === 0) return <p>Ainda ninguém marcou nada neste plano.</p>;
  return (
    <div className="flex flex-col gap-3">
      <WorkTable people={people} />
      {networks.length > 0 ? (
        <p className="text-caption">
          Publicações por rede:{' '}
          {networks
            .map((n) => `${SOCIAL_NETWORK_LABELS[n.network]} ${String(n.count)}`)
            .join(' · ')}
        </p>
      ) : null}
    </div>
  );
}

export function WorkTable({
  people,
}: {
  people: { login: string; filmed: number; edited: number; approved: number; published: number }[];
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-caption">
        <thead>
          <tr className="border-b-2 border-gu-line">
            <th className="p-2">Pessoa</th>
            <th className="p-2">Filmou</th>
            <th className="p-2">Editou</th>
            <th className="p-2">Aprovou</th>
            <th className="p-2">Publicou</th>
          </tr>
        </thead>
        <tbody>
          {people.map((p) => (
            <tr key={p.login} className="border-b border-gu-line">
              <td className="p-2 font-semibold">{p.login}</td>
              <td className="p-2">{p.filmed}</td>
              <td className="p-2">{p.edited}</td>
              <td className="p-2">{p.approved}</td>
              <td className="p-2">{p.published}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
